import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogDescription,
} from "../../ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../ui/select";
import Button from "../../button-v1";
import styles from "./styles.module.css";
import { Checkbox } from "../../ui/checkbox";
import { FaPhone, FaMessage } from "react-icons/fa6";
import { toast } from "react-hot-toast";
import Input from "../../ui/input";

const contactSchema = z.object({
  label: z
    .string()
    .min(1, "Contact label is required")
    .max(50, "Contact label must be less than 50 characters"),
  number: z
    .string()
    .min(1, "Please enter a valid phone number")
    .regex(/^[0-9+\-\s()]*$/, "Please enter a valid phone number"),
  is_primary: z.boolean().default(false),
  number_type: z.enum(["messaging", "calling"], {
    message: "Please select a contact type",
  }),
  relation: z
    .enum(["landline", "work", "personal", "mobile", "relative"])
    .optional()
    .or(z.string().optional()),
});

const contactTypes = [
  { label: "Message", value: "messaging", icon: <FaMessage /> },
  { label: "Call", value: "calling", icon: <FaPhone /> },
];

const defaultSchema = {
  relation: "",
  label: "",
  number: "",
  number_type: "",
  is_primary: false,
};

const AddContactsView = ({
  open,
  setOpen,
  contactId,
  contactInfo,
  numbers = [],
  setNumbers,
}) => {
  const normalizeNumber = (value) => (value || "").replace(/\D/g, "");

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    setError,
    watch,
    reset,
  } = useForm({
    resolver: zodResolver(contactSchema),
    defaultValues: defaultSchema,
  });

  const number_type = watch("number_type");
  const relation = watch("relation");
  const is_primary = watch("is_primary");

  const onSubmit = (data) => {
    if (!setNumbers) {
      toast.error("Numbers update handler not provided");
      return;
    }

    const target = normalizeNumber(data.number);
    const hasDuplicate = (Array.isArray(numbers) ? numbers : []).some((c) => {
      const current = normalizeNumber(c.number);
      if (contactId && current === normalizeNumber(contactId)) return false;
      return current === target;
    });
    if (hasDuplicate) {
      toast.error("This contact number already added");
      return;
    }

    if (contactId) {
      setNumbers((prev) => {
        const next = Array.isArray(prev) ? [...prev] : [];
        const idx = next.findIndex(
          (c) => normalizeNumber(c.number) === normalizeNumber(contactId)
        );
        if (idx !== -1) {
          next[idx] = { ...next[idx], ...data };
          //   toast.success("Contact updated successfully");
          return next;
        }
        // If not found but we have contactId, append as safety
        // toast.success("Contact added successfully");
        return [...next, { ...data }];
      });
    } else {
      setNumbers((prev) => {
        const next = Array.isArray(prev) ? [...prev] : [];
        return [...next, { ...data }];
      });
      //   toast.success("Contact added successfully");
    }
    setOpen(false);
  };

  const onDelete = () => {
    if (!setNumbers) {
      toast.error("Numbers update handler not provided");
      return;
    }
    if (!contactId) return;
    setNumbers((prev) => {
      const next = (Array.isArray(prev) ? prev : []).filter(
        (c) => normalizeNumber(c.number) !== normalizeNumber(contactId)
      );
      return next;
    });
    // toast.success("Contact deleted successfully");
    setOpen(false);
  };

  useEffect(() => {
    if (open) {
      // Populate values when editing
      const source = contactInfo
        ? contactInfo
        : Array.isArray(numbers)
        ? numbers.find(
            (c) => normalizeNumber(c.number) === normalizeNumber(contactId)
          )
        : undefined;

      if (source) {
        setValue("label", source.label || "");
        setValue("number", source.number || "");
        setValue("number_type", source.number_type || "");
        setValue("is_primary", !!source.is_primary);
        setValue("relation", source.relation || "");
      } else {
        reset();
      }
    }
  }, [open, contactId, contactInfo, numbers]);

  return (
    <Dialog open={open} setOpen={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Contact Number</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form className={styles.input_fields}>
            <div className={styles.input_wrapper}>
              <label className="label">Contact Label</label>
              <input
                type="text"
                placeholder="Enter contact label"
                className="input_field"
                {...register("label")}
              />
              {errors.label && (
                <span className="error-message">{errors.label.message}</span>
              )}
            </div>

            <div className={styles.input_wrapper}>
              <label className="label">Contact Number</label>
              <Controller
                name="number"
                control={control}
                render={({ field }) => (
                  <Input
                    mask={true}
                    maskPattern="(999) 999-9999"
                    type="tel"
                    placeholder="Enter contact number"
                    name={field.name}
                    value={field.value || ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  />
                )}
              />
              {errors.number && (
                <span className="error-message">{errors.number.message}</span>
              )}
            </div>

            <section className={styles.input_container}>
              <div className={styles.input_wrapper}>
                <label className="label">Contact Type</label>
                <Select
                  value={number_type}
                  onChange={(value) => setValue("number_type", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a contact type">
                      <p>
                        {contactTypes.find((type) => type.value === number_type)
                          ?.label || "Select a contact type"}
                      </p>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {contactTypes.map((type) => (
                      <SelectItem
                        key={type.value}
                        value={type.value}
                        className={styles.select_item}
                      >
                        <span>{type.icon}</span>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.number_type && (
                  <span className="error-message">
                    {errors.number_type.message}
                  </span>
                )}
              </div>
            </section>

            <div className={styles.input_wrapper}>
              <section className={styles.checkbox_wrapper}>
                <section>
                  <Checkbox
                    size="large"
                    checked={relation === "landline"}
                    onCheckedChange={(checked) => {
                      setValue("relation", "landline");
                    }}
                  />
                  <label>Landline</label>
                </section>
                <section>
                  <Checkbox
                    size="large"
                    checked={relation === "work"}
                    onCheckedChange={(checked) => {
                      setValue("relation", "work");
                    }}
                  />
                  <label>Work</label>
                </section>
                <section>
                  <Checkbox
                    size="large"
                    checked={relation === "personal"}
                    onCheckedChange={(checked) => {
                      setValue("relation", "personal");
                    }}
                  />
                  <label>Personal</label>
                </section>
                <section>
                  <Checkbox
                    size="large"
                    checked={relation === "mobile"}
                    onCheckedChange={(checked) => {
                      setValue("relation", "mobile");
                    }}
                  />
                  <label>Mobile</label>
                </section>
                <section>
                  <Checkbox
                    size="large"
                    checked={relation === "relative"}
                    onCheckedChange={(checked) => {
                      setValue("relation", "relative");
                    }}
                  />
                  <label>Relative</label>
                </section>
              </section>
              {errors.relation && (
                <span className="error-message">{errors.relation.message}</span>
              )}
            </div>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button variant="normal" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          {contactId ? (
            <>
              {/* <Button variant="destructive" onClick={onDelete}>
                Delete Contact
              </Button> */}
              <Button
                variant="primary"
                type="submit"
                onClick={handleSubmit(onSubmit)}
              >
                Update Contact
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              type="submit"
              onClick={handleSubmit(onSubmit)}
            >
              Add Contact
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddContactsView;
